import * as React from 'react'

import { Box, Form, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import { Delete, Pencil } from '@factorialco/f0-react/icons/app'
import { useFormik } from 'formik'
import { GetEvaluationFormQuery } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { type EvaluationFormQuestionError } from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'

type AtsEvaluationFormQuestion = NonNullable<
  NonNullable<NonNullable<GetEvaluationFormQuery['ats']>['evaluationForm']>['questions']
>[0]

interface QuestionItemProps {
  value: AtsEvaluationFormQuestion
  onEdit: (newValue: AtsEvaluationFormQuestion) => void
  onDelete: () => void
  errors: EvaluationFormQuestionError
  isLastQuestion: boolean
}

export const QuestionItem = ({
  value,
  onEdit,
  onDelete,
  errors,
  isLastQuestion,
}: QuestionItemProps) => {
  const isNew = isEmpty(value.text)
  const [isEditing, setIsEditing] = React.useState(isNew)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  if (isEditing) {
    return (
      <QuestionForm
        value={value}
        onChange={(newValue) => {
          setIsEditing(false)
          onEdit(newValue)
        }}
        onCancel={() => (isNew ? onDelete() : setIsEditing(false))}
        errors={errors}
        isLastQuestion={isLastQuestion}
        isNew={isNew}
      />
    )
  }

  return (
    <Box
      flexDirection="row"
      alignItems="flexStart"
      gap="s8"
      background="grey100"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
      paddingX="s16"
      paddingY="s8"
    >
      <Box flexGrow>
        <Text.Normal color="grey1000" weight="medium">
          {value.text}
        </Text.Normal>
        <Text.Normal>{value.description}</Text.Normal>
      </Box>

      <Box flexDirection="row" alignItems="center" gap="s8" paddingY="s8">
        <F0Button
          variant="outline"
          onClick={() => setIsEditing(true)}
          icon={Pencil}
          hideLabel
          label={i18n.t('ats.evaluation_forms.form.edit_skill', { name: value.text })}
        />
        <F0Button
          variant="outline"
          onClick={() => setDeleteModalOpen(true)}
          icon={Delete}
          hideLabel
          label={i18n.t('ats.evaluation_forms.form.delete_skill.delete', { name: value.text })}
        />
      </Box>

      {deleteModalOpen && (
        <Modal
          illustration="delete"
          title={i18n.t('ats.evaluation_forms.form.delete_skill.confirm_title')}
          onClose={() => setDeleteModalOpen(false)}
          onSubmit={onDelete}
          footer={
            <Box flexDirection="row" gap="s16">
              <F0Button
                onClick={() => setDeleteModalOpen(false)}
                variant="outline"
                label={i18n.t('ats.evaluation_forms.form.delete_skill.confirm_cancel')}
              />
              <F0Button
                type="submit"
                label={i18n.t('ats.evaluation_forms.form.delete_skill.confirm_submit')}
              />
            </Box>
          }
        />
      )}
    </Box>
  )
}

interface QuestionFormProps {
  value: AtsEvaluationFormQuestion
  onChange: (newValue: AtsEvaluationFormQuestion) => void
  onCancel: () => void
  errors: EvaluationFormQuestionError
  isLastQuestion: boolean
  isNew: boolean
}

const QuestionForm = ({
  value,
  onChange,
  onCancel,
  errors,
  isLastQuestion,
  isNew,
}: QuestionFormProps) => {
  const form = useFormik<AtsEvaluationFormQuestion>({
    initialValues: {
      id: value.id,
      text: value.text,
      description: value.description,
    },
    validate(data) {
      const errors: EvaluationFormQuestionError = {}

      if (!data.text) {
        errors.text = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit(data) {
      onChange(data)
    },
  })

  useEffect(() => {
    if (!isEmpty(errors) && !form.dirty) {
      form.setErrors(errors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty(errors), form.dirty])

  return (
    <Form onSubmit={form.handleSubmit} withBackground>
      <Input
        required
        label={i18n.t('ats.evaluation_forms.form.skill_label')}
        placeholder={i18n.t('ats.evaluation_forms.form.skill_placeholder')}
        value={form.values.text}
        onChange={(text) => form.setFieldValue('text', text)}
        error={form.errors.text}
        size="md"
      />
      <Textarea
        label={i18n.t('ats.evaluation_forms.form.questions_label')}
        placeholder={i18n.t('ats.evaluation_forms.form.questions_placeholder')}
        value={form.values.description ?? ''}
        onChange={(description) => form.setFieldValue('description', description)}
        rows={3}
      />

      <Form.Buttons>
        {(!isLastQuestion || !isNew) && (
          <F0Button onClick={onCancel} variant="outline" label={i18n.t('common.cancel')} />
        )}

        <F0Button type="submit" label={i18n.t('common.save')} />
      </Form.Buttons>
    </Form>
  )
}
